import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AlertMessage from '../utilities/AlertMessage';
import { ReactComponent as InfoIcon } from '../utilities/icons/info-stroke.svg';
import { ReactComponent as RightArrow } from '../utilities/icons/arrow-right.svg';
import useSearchParams from '../../hooks/useSearchParams';
import { PUBLICITIES } from '../../Constants';
import './RecordCollectionInformation.css';

const hasCreator = (collection) => {
  const legacyCreator = typeof collection.creator === 'string' && collection.creator.trim();
  const creators = collection.creators?.some(creator => (
    typeof creator === 'string' ? creator.trim() : creator?.displayName?.trim() || creator?.userName?.trim()
  ));
  return Boolean(legacyCreator || creators);
};

const hasAdministratingPerson = (collection) => {
  const persons = [
    ...(Array.isArray(collection.persons) ? collection.persons : []),
    ...(Array.isArray(collection.contributors) ? collection.contributors : [])
  ];
  return persons.some(person => {
    if (typeof person !== 'string') {
      return Boolean(person?.userName || person?.eppn);
    }
    return person.trim() && !person.startsWith('grp-');
  });
};

const hasRequiredInformation = (collection) => (
  Boolean(collection.title?.trim()) &&
  Boolean(collection.description?.trim()) &&
  hasCreator(collection) &&
  hasAdministratingPerson(collection) &&
  PUBLICITIES.some(publicity => publicity.value === collection.published) &&
  Boolean(collection.contentType) &&
  collection.science !== '' && collection.science !== null && collection.science !== undefined &&
  Boolean(collection.facultyDepartment)
);

const RecordCollectionInformation = ({ collection }) => {
  const { t } = useTranslation();
  const [_searchParams, setSearchParams] = useSearchParams();

  if (!collection?.identifier || /^inbox\s/i.test(collection.title || '') ||
      hasRequiredInformation(collection)) {
    return null;
  }

  const goToCollection = (event) => {
    event.preventDefault();
    setSearchParams({ collection: collection.identifier });
  };

  return (
    <div className="record-collection-information">
      <AlertMessage
        type="info"
        iconComponent={InfoIcon}
        iconClassName="record-collection-information-icon"
      >
        <>
          {t('record_collection_missing_information')}{' '}
          <a
            href={`?collection=${collection.identifier}`}
            onClick={goToCollection}
            aria-label={t('record_collection_missing_information_link_aria_label', {
              collectionTitle: collection.title
            })}
          >
            {t('record_collection_missing_information_link')}
            <RightArrow className="record-collection-information-arrow" aria-hidden="true" />
          </a>
        </>
      </AlertMessage>
    </div>
  );
};

RecordCollectionInformation.propTypes = {
  collection: PropTypes.object
};

export default RecordCollectionInformation;
